import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as GPTHUB } from "../../assets/icons/gpthub.svg";

import {
  HEADER_CREATE_BUTTON,
  HEADER_LOGIN_BUTTON,
} from "../../constants/constants";

import { clearUserInfo, hideIdleModal, hideLoginModal, logout, showLoginModal } from "../../store/auth/authSlice";
import { setClearClaimDetails } from "../../store/claim/claimSlice";

import useScreenSize from "../../Hooks/useScreenSize";
import { Button } from "../common";
import UserProfileDropdown from "../public/UserProfileHeader";
import { handlePromptStripePayment } from "../../utils/Helper";
import { useLogOutUserMutation } from "../../store/auth/usersApiSlice";

const AdminNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { isMobile } = useScreenSize();
  const { userInfo, token } = useSelector((state) => state.auth);
  const [logOutUser] = useLogOutUserMutation();
  const activeTab = useSelector((state) => state.activeTab.activeTab);

  const isPrompt = location.pathname.includes("prompt");
  const { REACT_APP_GPT_PROMPT_PAYMENT_LINK, REACT_APP_GPT_URL } = process.env;

  const toggleDrawer = () => setIsOpen((prev) => !prev);

  const handleLogout = async(e) => {
    e.preventDefault();
    await logOutUser({token});
    dispatch(hideIdleModal());
    dispatch(logout());
    dispatch(hideLoginModal());
    dispatch(setClearClaimDetails());
    dispatch(clearUserInfo());
    navigate(isPrompt ? "/login/prompt" : "/login");
    setIsOpen(false);
  };

  /** 🔹 GPT HUB LOGIC (single source of truth) */
  const handleGptClick = () => {
    const isGPT = userInfo.has_gpt;
    const url = isGPT
      ? "/dashboard/gpt"
      : `${REACT_APP_GPT_URL}/gpt-prompts`;
    isGPT ? navigate(url) : window.open(url, "_blank");
    setIsOpen(false); // close drawer on mobile
  };

  return (
    <>
      {/* ================= NAVBAR ================= */}
      <nav className="bg-white border-b shadow-lg px-4 lg:px-6 py-2">
        <div className="max-w-7xl mx-auto flex items-center justify-between">

          {/* LEFT */}
          <Link
            to="/"
            className="text-[#3C4848] text-[20px] font-medium"
          >
            {activeTab}
          </Link>

          {/* RIGHT */}
          <div className="flex items-center gap-3">

            {/* Desktop Login / Demo */}
            {!isMobile && !userInfo && (
              <>
                <Link
                  to={isPrompt ? "/login/prompt" : "/login"}
                  className="text-primary text-sm font-medium"
                >
                  {HEADER_LOGIN_BUTTON}
                </Link>

                <Button classes="bg-button text-white px-6 py-2 text-sm">
                  Schedule Demo
                </Button>
              </>
            )}

            {/* GPT HUB (Desktop) */}
            {/* <div
              className="hidden sm:block p-[1px] rounded"
              style={{
                background:
                  "linear-gradient(97.76deg, #874BFF 1.71%, #FF307C 99.55%)",
              }}
            >
              <button
                onClick={handleGptClick}
                className="flex items-center gap-2 rounded px-4 py-2 bg-[linear-gradient(97.76deg,#F5F0FF_1.71%,#FFE5EF_99.55%)]"
              >
                <GPTHUB />
                <span
                  className="bg-clip-text text-transparent text-sm font-semibold"
                  style={{
                    backgroundImage:
                      "linear-gradient(97.76deg, #874BFF 1.71%, #FF307C 99.55%)",
                  }}
                >
                  GPT Hub
                </span>
              </button>
            </div> */}

            {/* Profile */}
            {userInfo && <UserProfileDropdown showDetails />}

            {/* Hamburger */}
            <button
              onClick={toggleDrawer}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <svg width="28" height="28" viewBox="0 0 32 32">
                <path d="M4 9H28" stroke="#6E6E6E" strokeWidth="2" />
                <path d="M4 16H28" stroke="#6E6E6E" strokeWidth="2" />
                <path d="M4 23H28" stroke="#6E6E6E" strokeWidth="2" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* ================= BACKDROP ================= */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30"
          onClick={toggleDrawer}
        />
      )}

      {/* ================= DRAWER ================= */}
      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-80 bg-white p-4 transition-transform duration-300 ${isOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        {/* Close */}
        <button
          onClick={toggleDrawer}
          className="absolute top-3 right-3 text-gray-500"
        >
          ✕
        </button>

        <div className="flex flex-col gap-4 mt-12 text-left">

          {/* 🔹 GPT HUB (Mobile Sidebar) */}
          {/* <div
            className="p-[1px] rounded w-[141px]"
            style={{
              background:
                "linear-gradient(97.76deg, #874BFF 1.71%, #FF307C 99.55%)",
            }}
          >
            <button
              onClick={handleGptClick}
              className="w-full flex items-center gap-3 rounded px-4 py-3 bg-[linear-gradient(97.76deg,#F5F0FF_1.71%,#FFE5EF_99.55%)]"
            >
              <GPTHUB />
              <span
                className="bg-clip-text text-transparent text-sm font-semibold"
                style={{
                  backgroundImage:
                    "linear-gradient(97.76deg, #874BFF 1.71%, #FF307C 99.55%)",
                }}
              >
                GPT Hub
              </span>
            </button>
          </div> */}

          {!userInfo ? (
            <Link
              to={isPrompt ? "/login/prompt" : "/login"}
              onClick={toggleDrawer}
              className="text-primary font-medium"
            >
              Log In
            </Link>
          ) : (
            <button
              onClick={handleLogout}
              className="text-primary font-medium text-left"
            >
              Log Out
            </button>
          )}

          {userInfo && (
            <Link
              to="/dashboard"
              onClick={toggleDrawer}
              className="text-primary font-medium"
            >
              Dashboard
            </Link>
          )}

          {!isPrompt ? (
            <Link
              to="/sign-up"
              onClick={toggleDrawer}
              className="bg-primary text-white text-center py-2 rounded-full"
            >
              {HEADER_CREATE_BUTTON}
            </Link>
          ) : (
            <Button
              onClick={() =>
                handlePromptStripePayment(
                  REACT_APP_GPT_PROMPT_PAYMENT_LINK
                )
              }
              classes="border border-[#002940] text-[#002940] py-2 rounded"
            >
              Get Started
            </Button>
          )}
        </div>
      </aside>
    </>
  );
};

export default AdminNavbar;
